import React, { type Dispatch, type SetStateAction } from "react";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";

import type { AmazonIntegration } from "src/interfaces/plaidIntegration.interface";
import { type DataType } from "src/pages/GetStarted/ImportDialog";
import { isAmazonAllowedToDirectImportSelector } from "src/store/uploads/selector";
import { amazonIntegrationDelete } from "src/apiService/modules/amazon";
import {
  amazonSyncApi,
  amazonDirectImportInventory,
  amazonDirectImportSales,
  amazonIntegrationSyncChange,
} from "src/apiService/modules/amazon";
import { bankAccountByIntegrationIdSelector } from "src/store/bankAccount/selector";
import { setActiveDialog } from "src/store/adminHtml/actions";

import type { DialogState } from "../types";
import PlatformIntegrationsTableRow from "../PlatformIntegrationsTableRow";
import useIsRunning from "../useIsRunning";

// fix-vim-highlight = }

const IsRunningThreshold = 10 * 60 * 1000; // 10 min
const DirectImportThreshold = 2 * 60 * 60 * 1000;

export function usePreventImportAmazon(dataType: DataType) {
  return !useSelector((state: any) =>
    isAmazonAllowedToDirectImportSelector(state, dataType)
  );
}

function AmazonIntegrationTableRow({
  integration,
  setDialog,
  hideInventory,
}: {
  integration: AmazonIntegration;
  setDialog: Dispatch<SetStateAction<DialogState>>;
  hideInventory: boolean;
}) {
  const dispatch = useDispatch();
  const isRunning = useIsRunning(
    Math.max(
      integration.salesIsRunning || 0,
      (integration.reports?.directImportSales?.createdAt?.toDate()?.valueOf() ||
        0) + DirectImportThreshold,
      (integration.reports?.dailySyncSales?.createdAt?.toDate()?.valueOf() ||
        0) + DirectImportThreshold
    ),
    Math.max(
      integration.inventoryIsRunning || 0,
      (integration.reports?.directImportInventory?.createdAt
        ?.toDate()
        ?.valueOf() || 0) + DirectImportThreshold,
      (integration.reports?.dailySyncInventory?.createdAt
        ?.toDate()
        ?.valueOf() || 0) + DirectImportThreshold
    ),
    IsRunningThreshold
  );
  const bankAccount = useSelector((state) =>
    integration?.id
      ? bankAccountByIntegrationIdSelector(state, integration.id)
      : undefined
  );

  return (
    <PlatformIntegrationsTableRow
      key={integration.id}
      name={bankAccount?.name || "Amazon"}
      platform="Amazon"
      isRunning={isRunning}
      sync={integration.sync}
      inventory={integration.sync && integration.inventory !== false}
      showReconnectAction={!!integration.error}
      onOpenNotifications={() => dispatch(setActiveDialog("notifications"))}
      onPullDataNow={async () => {
        await amazonSyncApi(integration.id);
        toast.success("Request received. Your imports are processing.");
      }}
      onImport={() => {
        setDialog({
          type: "import",
          props: {
            integrationId: integration.id,
            fetchListings: amazonDirectImportInventory,
            fetchTransactions: amazonDirectImportSales,
            providerName: "Amazon",
            usePreventImport: usePreventImportAmazon,
          },
        });
      }}
      onSyncChanged={async (sync, inventory) => {
        await amazonIntegrationSyncChange(integration.id, sync, inventory);
        if ((inventory === undefined || inventory) && sync)
          amazonSyncApi(integration.id);
      }}
      hideInventory={hideInventory}
      onDelete={() =>
        setDialog({
          type: "delete",
          props: {
            integration,
            deleteIntegration: amazonIntegrationDelete,
          },
        })
      }
    />
  );
}

export default AmazonIntegrationTableRow;
